/**
 * Financial reports with configurable scope (date range, property, owner, basis),
 * drill-down to transactions and CSV/print export.
 */
import { ACCOUNTING_BASES, EXPENSE_TREATMENTS } from "@shared/constants.ts";
import type { BucketTotals, Summary } from "@shared/finance/ledger.ts";
import type { Granularity } from "@shared/periods.ts";
import type { ReactNode } from "react";
import { Button, Card, Col, Row, Table } from "react-bootstrap";
import { Link } from "react-router";
import { BreakdownChart, IncomeExpenseChart, PropertyProfitChart } from "../../components/charts/Charts.tsx";
import { GRANULARITIES, SummaryCards } from "../../components/Financials.tsx";
import { DateRangePicker } from "../../components/ui/DateRangePicker.tsx";
import { FilterBar, FilterSelect } from "../../components/ui/Filters.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  Can,
  EmptyState,
  Money,
  PageHeader,
  QueryState,
  Section,
  StatusBadge,
} from "../../components/ui/kit.tsx";
import { type Params, withQuery } from "../../lib/api.ts";
import { useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi } from "../../lib/query.ts";
import { useDateRange, useUrlState } from "../../lib/url-state.ts";

interface Group {
  key: string;
  label: string;
  summary: Summary;
}

interface Meta {
  range: { from: string; to: string };
  basis: string;
  owner: { id: string; name: string } | null;
}

/** Report scope from the URL: range + optional property/owner/basis. */
function useScope(): Params {
  const range = useDateRange();
  const { get } = useUrlState();
  return {
    from: range.from,
    to: range.to,
    propertyId: get("propertyId"),
    ownerId: get("ownerId"),
    basis: get("basis"),
  };
}

function ScopeBar({
  extra,
  exportPath,
  exportParams,
}: {
  extra?: ReactNode;
  exportPath?: string;
  exportParams?: Params;
}) {
  const range = useDateRange();
  const lookups = useLookups();
  const scope = useScope();
  return (
    <Card className="mb-4 no-print">
      <FilterBar
        actions={
          <>
            {exportPath && (
              <Can permission="reports:export">
                <a
                  className="btn btn-sm btn-outline-secondary"
                  href={withQuery(exportPath, { ...scope, ...exportParams, format: "csv" })}
                >
                  <Icon name="download" size={14} /> CSV
                </a>
              </Can>
            )}
            <Button size="sm" variant="outline-secondary" onClick={() => window.print()}>
              Print / PDF
            </Button>
          </>
        }
      >
        <DateRangePicker range={range} />
        <FilterSelect param="propertyId" label="Property" options={toOptions(lookups?.properties)} />
        <FilterSelect
          param="ownerId"
          label="Owner"
          options={toOptions(lookups?.owners)}
          allLabel="Whole portfolio"
        />
        <FilterSelect param="basis" label="Basis" options={ACCOUNTING_BASES} allLabel="Default" />
        {extra}
      </FilterBar>
    </Card>
  );
}

function MetaLine({ meta }: { meta: Meta }) {
  const f = useFormat();
  return (
    <p className="small text-muted">
      {f.date(meta.range.from)} – {f.date(meta.range.to)} · {f.label(ACCOUNTING_BASES, meta.basis)}
      {meta.owner && <> · {meta.owner.name}'s share</>}
    </p>
  );
}

const REPORTS = [
  {
    to: "/reports/pnl",
    title: "Profit & loss",
    body: "Income, expenses and net profit per property and for the portfolio.",
    icon: "chart",
  },
  { to: "/reports/income", title: "Income", body: "Income by property, date and type.", icon: "income" },
  {
    to: "/reports/expenses",
    title: "Expenses",
    body: "Expenses by property, category, supplier and date.",
    icon: "expense",
  },
  {
    to: "/reports/rent",
    title: "Rent",
    body: "Rent expected vs received, arrears and payment history.",
    icon: "key",
  },
  {
    to: "/reports/performance",
    title: "Property performance",
    body: "Compare properties side by side: net, yield and occupancy.",
    icon: "home",
  },
  {
    to: "/reports/custom",
    title: "Custom report",
    body: "Group figures by any dimension and period.",
    icon: "settings",
  },
  {
    to: "/reports/transactions",
    title: "Transactions",
    body: "Every transaction behind the numbers.",
    icon: "file",
  },
];

export function ReportsHomePage() {
  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="All reports respect the date range, property, owner and accounting basis you choose."
      />
      <Row className="g-3">
        {REPORTS.map((r) => (
          <Col md={6} xl={4} key={r.to}>
            <Link to={r.to} className="text-reset text-decoration-none">
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex gap-2 align-items-center mb-2">
                    <Icon name={r.icon} />
                    <h2 className="h6 mb-0">{r.title}</h2>
                  </div>
                  <p className="small text-muted mb-0">{r.body}</p>
                </Card.Body>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>
    </>
  );
}

function SummaryRowCells({ s }: { s: Summary }) {
  return (
    <>
      <td className="text-end">
        <Money pence={s.incomePence} kind="income" />
      </td>
      <td className="text-end">
        <Money pence={s.operatingExpensesPence} kind="expense" />
      </td>
      <td className="text-end">
        <Money pence={s.financeCostsPence} kind="expense" />
      </td>
      <td className="text-end">
        <Money pence={s.replacementItemsPence} kind="expense" />
      </td>
      <td className="text-end fw-semibold">
        <Money pence={s.netPence} kind="net" />
      </td>
      <td className="text-end text-muted">
        <Money pence={s.capitalPence} />
      </td>
    </>
  );
}

const SUMMARY_HEADERS = (
  <>
    <th className="text-end">Income</th>
    <th className="text-end">Operating expenses</th>
    <th className="text-end">Finance costs</th>
    <th className="text-end">Replacement items</th>
    <th className="text-end">Net profit / loss</th>
    <th className="text-end">Capital (excluded)</th>
  </>
);

export function PnlReportPage() {
  const scope = useScope();
  const q = useApi<Meta & { summary: Summary; properties: Group[] }>("/api/reports/pnl", scope, {
    keepPrevious: true,
  });
  return (
    <>
      <PageHeader
        title="Profit & loss"
        breadcrumbs={[{ label: "Reports", to: "/reports" }, { label: "Profit & loss" }]}
      />
      <ScopeBar exportPath="/api/reports/pnl" />
      <QueryState query={q}>
        {(r) => (
          <>
            <MetaLine meta={r} />
            <SummaryCards summary={r.summary} scope={scope} />
            <Section title="By property" flush>
              <Table responsive className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Property</th>
                    {SUMMARY_HEADERS}
                  </tr>
                </thead>
                <tbody>
                  {r.properties.map((p) => (
                    <tr key={p.key}>
                      <td>
                        <Link to={withQuery("/reports/transactions", { ...scope, propertyId: p.key })}>
                          {p.label}
                        </Link>
                      </td>
                      <SummaryRowCells s={p.summary} />
                    </tr>
                  ))}
                  {r.properties.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-muted">
                        No transactions in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="fw-bold">
                    <td>Portfolio total</td>
                    <SummaryRowCells s={r.summary} />
                  </tr>
                </tfoot>
              </Table>
            </Section>
            {r.properties.length > 0 && (
              <Section title="Chart">
                <PropertyProfitChart groups={r.properties} />
              </Section>
            )}
            <p className="small text-muted">
              Net profit = income − operating expenses − finance costs − replacement of domestic items.
              Capital expenditure, private costs and non-property receipts are shown separately and excluded.
            </p>
          </>
        )}
      </QueryState>
    </>
  );
}

function GranularitySelect() {
  return <FilterSelect param="granularity" label="Group by" options={GRANULARITIES} allLabel="Monthly" />;
}

function GroupTable({
  title,
  groups,
  value,
  kind,
  drill,
}: {
  title: string;
  groups: Group[];
  value: (s: Summary) => number;
  kind: "income" | "expense";
  drill: (g: Group) => string;
}) {
  const total = groups.reduce((s, g) => s + value(g.summary), 0);
  return (
    <Section title={title} flush>
      <Table className="mb-0">
        <tbody>
          {groups.map((g) => (
            <tr key={g.key}>
              <td>
                <Link to={drill(g)}>{g.label}</Link>
              </td>
              <td className="text-end">
                <Money pence={value(g.summary)} kind={kind} />
              </td>
              <td className="text-end text-muted small" style={{ width: 70 }}>
                {total ? `${Math.round((value(g.summary) / total) * 100)}%` : ""}
              </td>
            </tr>
          ))}
          {groups.length === 0 && (
            <tr>
              <td className="text-muted small">Nothing in this period.</td>
            </tr>
          )}
        </tbody>
      </Table>
    </Section>
  );
}

export function IncomeReportPage() {
  const scope = useScope();
  const { get } = useUrlState();
  const granularity = (get("granularity") || "month") as Granularity;
  const q = useApi<
    Meta & { totalPence: number; byProperty: Group[]; byType: Group[]; byPeriod: BucketTotals[] }
  >("/api/reports/income", { ...scope, granularity }, { keepPrevious: true });
  const value = (s: Summary) => s.incomePence + s.nonPropertyReceiptsPence;
  return (
    <>
      <PageHeader
        title="Income report"
        breadcrumbs={[{ label: "Reports", to: "/reports" }, { label: "Income" }]}
      />
      <ScopeBar
        extra={<GranularitySelect />}
        exportPath="/api/reports/income"
        exportParams={{ granularity }}
      />
      <QueryState query={q}>
        {(r) => (
          <>
            <MetaLine meta={r} />
            <h2 className="h4 mb-3">
              Total <Money pence={r.totalPence} kind="income" />
            </h2>
            <Section title="Income over time">
              <IncomeExpenseChart buckets={r.byPeriod} show="income" />
            </Section>
            <Row>
              <Col lg={6}>
                <GroupTable
                  title="By property"
                  groups={r.byProperty}
                  value={value}
                  kind="income"
                  drill={(g) =>
                    withQuery("/reports/transactions", { ...scope, kind: "income", propertyId: g.key })
                  }
                />
              </Col>
              <Col lg={6}>
                <GroupTable
                  title="By income type"
                  groups={r.byType}
                  value={value}
                  kind="income"
                  drill={(g) =>
                    withQuery("/reports/transactions", { ...scope, kind: "income", typeKey: g.key })
                  }
                />
                <Section title="Breakdown">
                  <BreakdownChart
                    items={r.byType.map((g) => ({ label: g.label, pence: value(g.summary) }))}
                  />
                </Section>
              </Col>
            </Row>
          </>
        )}
      </QueryState>
    </>
  );
}

export function ExpenseReportPage() {
  const scope = useScope();
  const f = useFormat();
  const { get } = useUrlState();
  const granularity = (get("granularity") || "month") as Granularity;
  const q = useApi<
    Meta & {
      totalPence: number;
      byProperty: Group[];
      byCategory: Group[];
      bySupplier: Group[];
      byTreatment: Group[];
      byPeriod: BucketTotals[];
    }
  >("/api/reports/expenses", { ...scope, granularity }, { keepPrevious: true });
  const value = (s: Summary) => s.expensesPence + s.capitalPence + s.nonAllowablePence;
  return (
    <>
      <PageHeader
        title="Expense report"
        breadcrumbs={[{ label: "Reports", to: "/reports" }, { label: "Expenses" }]}
      />
      <ScopeBar
        extra={<GranularitySelect />}
        exportPath="/api/reports/expenses"
        exportParams={{ granularity }}
      />
      <QueryState query={q}>
        {(r) => (
          <>
            <MetaLine meta={r} />
            <h2 className="h4 mb-3">
              Total spent <Money pence={r.totalPence} kind="expense" />
            </h2>
            <Row className="g-3 mb-4">
              {r.byTreatment.map((t) => (
                <Col key={t.key} sm={6} lg={3}>
                  <Card>
                    <Card.Body>
                      <div className="small text-muted">{f.label(EXPENSE_TREATMENTS, t.key)}</div>
                      <div className="fw-bold">
                        <Money pence={value(t.summary)} />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <Section title="Expenses over time">
              <IncomeExpenseChart buckets={r.byPeriod} show="expenses" />
            </Section>
            <Row>
              <Col lg={6}>
                <GroupTable
                  title="By category"
                  groups={r.byCategory}
                  value={value}
                  kind="expense"
                  drill={(g) =>
                    withQuery("/reports/transactions", { ...scope, kind: "expense", typeKey: g.key })
                  }
                />
                <Section title="Breakdown">
                  <BreakdownChart
                    items={r.byCategory.map((g) => ({ label: g.label, pence: value(g.summary) }))}
                  />
                </Section>
              </Col>
              <Col lg={6}>
                <GroupTable
                  title="By property"
                  groups={r.byProperty}
                  value={value}
                  kind="expense"
                  drill={(g) =>
                    withQuery("/reports/transactions", { ...scope, kind: "expense", propertyId: g.key })
                  }
                />
                <GroupTable
                  title="By supplier"
                  groups={r.bySupplier}
                  value={value}
                  kind="expense"
                  drill={(g) =>
                    withQuery("/reports/transactions", { ...scope, kind: "expense", supplier: g.key })
                  }
                />
              </Col>
            </Row>
          </>
        )}
      </QueryState>
    </>
  );
}

interface RentRow {
  tenancyId: string;
  propertyLabel: string;
  tenantNames: string;
  startDate: string;
  endDate: string | null;
  expectedPence: number;
  receivedPence: number;
  outstandingPence: number;
  creditPence: number;
  payments: Array<{ id: string; date: string; amountPence: number }>;
}

export function RentReportPage() {
  const scope = useScope();
  const f = useFormat();
  const q = useApi<{
    range: Meta["range"];
    rows: RentRow[];
    totals: { expectedPence: number; receivedPence: number; outstandingPence: number; creditPence: number };
  }>("/api/reports/rent", scope, { keepPrevious: true });
  return (
    <>
      <PageHeader
        title="Rent report"
        subtitle="Rent due in the period, received in the period, and arrears at the end of the period"
        breadcrumbs={[{ label: "Reports", to: "/reports" }, { label: "Rent" }]}
      />
      <ScopeBar exportPath="/api/reports/rent" />
      <QueryState query={q}>
        {(r) => (
          <Section title="By tenancy" flush>
            <Table responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Property / tenants</th>
                  <th>Tenancy</th>
                  <th className="text-end">Expected</th>
                  <th className="text-end">Received</th>
                  <th className="text-end">Outstanding</th>
                  <th>Payments in period</th>
                </tr>
              </thead>
              <tbody>
                {r.rows.map((x) => (
                  <tr key={x.tenancyId}>
                    <td>
                      <Link to={`/tenancies/${x.tenancyId}`}>{x.propertyLabel}</Link>
                      <div className="small text-muted">{x.tenantNames}</div>
                    </td>
                    <td className="small">
                      {f.date(x.startDate)} – {x.endDate ? f.date(x.endDate) : "present"}
                    </td>
                    <td className="text-end">
                      <Money pence={x.expectedPence} />
                    </td>
                    <td className="text-end">
                      <Money pence={x.receivedPence} kind="income" />
                    </td>
                    <td className="text-end">
                      {x.outstandingPence > 0 ? (
                        <Money pence={x.outstandingPence} kind="outstanding" />
                      ) : x.creditPence > 0 ? (
                        <span className="small text-muted">Credit {f.money(x.creditPence)}</span>
                      ) : (
                        <StatusBadge status="paid" />
                      )}
                    </td>
                    <td className="small">
                      {x.payments.map((p) => `${f.date(p.date)} ${f.money(p.amountPence)}`).join(", ") || "—"}
                    </td>
                  </tr>
                ))}
                {r.rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-muted">
                      No tenancies in this period.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="fw-bold">
                  <td colSpan={2}>Total</td>
                  <td className="text-end">
                    <Money pence={r.totals.expectedPence} />
                  </td>
                  <td className="text-end">
                    <Money pence={r.totals.receivedPence} kind="income" />
                  </td>
                  <td className="text-end">
                    <Money pence={r.totals.outstandingPence} kind="outstanding" />
                  </td>
                  <td />
                </tr>
              </tfoot>
            </Table>
          </Section>
        )}
      </QueryState>
    </>
  );
}

type PerfRow = {
  propertyId: string;
  label: string;
  status: string;
  summary: Summary;
  occupancyPercent: number;
  purchasePricePence: number | null;
  netYieldPercent: number | null;
};
const PERF_SORTS: Record<string, (r: PerfRow) => number | string> = {
  label: (r) => r.label,
  income: (r) => r.summary.incomePence,
  expenses: (r) => r.summary.expensesPence,
  net: (r) => r.summary.netPence,
  occupancy: (r) => r.occupancyPercent,
  yield: (r) => r.netYieldPercent ?? Number.NEGATIVE_INFINITY,
};

export function PerformanceReportPage() {
  const scope = useScope();
  const { get, set } = useUrlState();
  const sortKey = get("sort") || "label";
  const dir = get("dir") === "desc" ? -1 : 1;
  const q = useApi<Meta & { rows: PerfRow[] }>("/api/reports/performance", scope, { keepPrevious: true });
  const th = (key: string, label: string, end = true) => (
    <th className={end ? "text-end" : ""}>
      <button
        type="button"
        className="btn btn-link p-0 text-reset fw-semibold text-decoration-none"
        onClick={() => set({ sort: key, dir: sortKey === key && dir === 1 ? "desc" : "asc" })}
      >
        {label} {sortKey === key ? (dir === 1 ? "▲" : "▼") : "↕"}
      </button>
    </th>
  );
  return (
    <>
      <PageHeader
        title="Property performance"
        subtitle="Compare properties side by side. Choose any column to sort — there is no forced ranking."
        breadcrumbs={[{ label: "Reports", to: "/reports" }, { label: "Performance" }]}
      />
      <ScopeBar exportPath="/api/reports/performance" />
      <QueryState query={q}>
        {(r) => {
          const rows = [...r.rows].sort((a, b) => {
            const av = PERF_SORTS[sortKey]?.(a) ?? 0;
            const bv = PERF_SORTS[sortKey]?.(b) ?? 0;
            return av < bv ? -dir : av > bv ? dir : 0;
          });
          return (
            <>
              <MetaLine meta={r} />
              <Section title="Comparison" flush>
                <Table responsive className="mb-0 align-middle">
                  <thead>
                    <tr>
                      {th("label", "Property", false)}
                      {th("income", "Income")}
                      {th("expenses", "Expenses")}
                      {th("net", "Net")}
                      {th("occupancy", "Occupancy")}
                      {th("yield", "Net yield (annualised)")}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((x) => (
                      <tr key={x.propertyId}>
                        <td>
                          <Link to={`/properties/${x.propertyId}?tab=financials`}>{x.label}</Link>
                        </td>
                        <td className="text-end">
                          <Money pence={x.summary.incomePence} kind="income" />
                        </td>
                        <td className="text-end">
                          <Money pence={x.summary.expensesPence} kind="expense" />
                        </td>
                        <td className="text-end fw-semibold">
                          <Money pence={x.summary.netPence} kind="net" />
                        </td>
                        <td className="text-end">{x.occupancyPercent}%</td>
                        <td className="text-end">
                          {x.netYieldPercent === null ? (
                            <span className="text-muted small">No purchase price</span>
                          ) : (
                            `${x.netYieldPercent}%`
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Section>
              <Section title="Chart">
                <PropertyProfitChart groups={rows.map((x) => ({ label: x.label, summary: x.summary }))} />
              </Section>
            </>
          );
        }}
      </QueryState>
    </>
  );
}

const DIMENSIONS = [
  { value: "property", label: "Property" },
  { value: "line", label: "Report line" },
  { value: "category", label: "Category / income type" },
  { value: "hmrc", label: "HMRC category" },
  { value: "supplier", label: "Supplier / payer" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
  { value: "tax_year", label: "Tax year" },
];

export function CustomReportPage() {
  const scope = useScope();
  const { get, set } = useUrlState();
  const rows = get("rows") || "property";
  const columns = get("columns");
  const q = useApi<
    Meta & {
      rows: Group[];
      columns: Array<{ key: string; label: string }>;
      cells: Record<string, Record<string, Summary>>;
    }
  >("/api/reports/custom", { ...scope, rows, columns }, { keepPrevious: true });
  return (
    <>
      <PageHeader
        title="Custom report"
        subtitle="Group your figures any way you like"
        breadcrumbs={[{ label: "Reports", to: "/reports" }, { label: "Custom" }]}
      />
      <ScopeBar
        exportPath="/api/reports/custom"
        exportParams={{ rows, columns }}
        extra={
          <>
            <select
              className="form-select form-select-sm w-auto"
              aria-label="Rows"
              value={rows}
              onChange={(e) => set({ rows: e.target.value })}
            >
              {DIMENSIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  Rows: {d.label}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm w-auto"
              aria-label="Columns"
              value={columns}
              onChange={(e) => set({ columns: e.target.value })}
            >
              <option value="">Columns: summary</option>
              {DIMENSIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  Columns: {d.label}
                </option>
              ))}
            </select>
          </>
        }
      />
      <QueryState query={q}>
        {(r) => (
          <Section title="Results" flush>
            {r.rows.length === 0 ? (
              <EmptyState icon="chart" title="No data">
                There are no transactions for this scope.
              </EmptyState>
            ) : r.columns.length === 0 ? (
              <Table responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Group</th>
                    {SUMMARY_HEADERS}
                  </tr>
                </thead>
                <tbody>
                  {r.rows.map((g) => (
                    <tr key={g.key}>
                      <td>{g.label}</td>
                      <SummaryRowCells s={g.summary} />
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Table responsive className="mb-0 small">
                <thead>
                  <tr>
                    <th>Group (net)</th>
                    {r.columns.map((c) => (
                      <th key={c.key} className="text-end">
                        {c.label}
                      </th>
                    ))}
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {r.rows.map((g) => (
                    <tr key={g.key}>
                      <td>{g.label}</td>
                      {r.columns.map((c) => (
                        <td key={c.key} className="text-end">
                          {r.cells[g.key]?.[c.key] ? (
                            <Money pence={r.cells[g.key]![c.key]!.netPence} kind="net" />
                          ) : (
                            "—"
                          )}
                        </td>
                      ))}
                      <td className="text-end fw-semibold">
                        <Money pence={g.summary.netPence} kind="net" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
        )}
      </QueryState>
    </>
  );
}

interface Entry {
  id: string;
  kind: "income" | "expense";
  source: string;
  date: string;
  propertyLabel: string;
  typeLabel: string;
  supplierName: string | null;
  description: string;
  amountPence: number;
  hmrc: { key: string; label: string };
  treatment: string | null;
}

export function TransactionsPage() {
  const scope = useScope();
  const f = useFormat();
  const { get } = useUrlState();
  const drill: Params = {
    kind: get("kind"),
    line: get("line"),
    hmrc: get("hmrc"),
    typeKey: get("typeKey"),
    supplier: get("supplier"),
  };
  const q = useApi<Meta & { entries: Entry[]; totalPence: number }>(
    "/api/reports/transactions",
    { ...scope, ...drill },
    { keepPrevious: true },
  );
  const link = (e: Entry) =>
    e.source === "income" ? `/income/${e.id}` : e.source === "expense" ? `/expenses/${e.id}` : "/finance";
  const active = Object.entries(drill).filter(([, v]) => v);
  return (
    <>
      <PageHeader
        title="Transactions"
        subtitle="The individual transactions behind the figures"
        breadcrumbs={[{ label: "Reports", to: "/reports" }, { label: "Transactions" }]}
      />
      <ScopeBar
        exportPath="/api/reports/transactions"
        exportParams={drill}
        extra={
          <FilterSelect
            param="kind"
            label="Kind"
            options={[
              { value: "income", label: "Income" },
              { value: "expense", label: "Expenses" },
            ]}
          />
        }
      />
      {active.length > 0 && (
        <p className="small">
          Filtered by {active.map(([k]) => k).join(", ")} ·{" "}
          <Link to={withQuery("/reports/transactions", scope)}>clear drill-down</Link>
        </p>
      )}
      <QueryState query={q}>
        {(r) => (
          <Section title={`${r.entries.length} transactions`} flush>
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Property</th>
                  <th>Category</th>
                  <th>HMRC category</th>
                  <th>Description</th>
                  <th className="text-end">Amount in period</th>
                </tr>
              </thead>
              <tbody>
                {r.entries.map((e) => (
                  <tr key={`${e.source}-${e.id}`}>
                    <td>{f.date(e.date)}</td>
                    <td className="small">{e.propertyLabel}</td>
                    <td>
                      {e.typeLabel}
                      {e.treatment && e.treatment !== "revenue" && (
                        <div className="small text-muted">{f.label(EXPENSE_TREATMENTS, e.treatment)}</div>
                      )}
                    </td>
                    <td className="small text-muted">{e.hmrc.label}</td>
                    <td>
                      <Link to={link(e)}>
                        {[e.description, e.supplierName].filter(Boolean).join(" · ") || "View"}
                      </Link>
                    </td>
                    <td className="text-end">
                      <Money pence={e.amountPence} kind={e.kind} />
                    </td>
                  </tr>
                ))}
                {r.entries.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-muted">
                      No transactions.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="fw-bold">
                  <td colSpan={5}>Total</td>
                  <td className="text-end">
                    <Money pence={r.totalPence} />
                  </td>
                </tr>
              </tfoot>
            </Table>
          </Section>
        )}
      </QueryState>
    </>
  );
}
